import { ChatLayout } from "./components/ChatLayout";
import { ChatArchive } from "./components/ChatArchive";
import { ChatActions } from "./components/ChatActions";
import { AIConsentManager } from "./components/AIConsentManager";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";
import { RestorePrompt } from "./components/RestorePrompt";
import { ChatContainer } from "./components/ChatContainer";
import { useAIAssistant } from "./hooks/useAIAssistant";

export function AIAssistantInterface() {
  const {
    messages,
    userInput,
    setUserInput,
    isLoading,
    isGenerating,
    isAnalyzing,
    progress,
    showRestorePrompt,
    handleSendMessage,
    handleCreateDocument,
    handleFileAnalysis,
    handleSaveChat,
    savedChats,
    loadChat,
    handleDeleteChat,
    handleRenameChat,
    resetChat,
    handleCreateCase,
    handleLawSearch,
    setMessages,
    handleRestoreChat,
    handleStartNewChat
  } = useAIAssistant();

  const [showArchive, setShowArchive] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  
  // Check if we have a meaningful conversation
  const messagesExist = messages.length > 1;
  const hasUserMessage = messages.some(m => m.role === 'user');
  const hasAssistantMessage = messages.some(m => m.role === 'assistant' && m.id !== 'initial');
  const canShowServices = messagesExist && hasUserMessage && hasAssistantMessage;

  const handleSaveChatWrapper = async () => {
    await handleSaveChat(messages);
  };

  const handleLoadChatWrapper = async (chatId: string) => {
    const loadedMessages = await loadChat(chatId);
    if (loadedMessages) {
      setMessages(loadedMessages);
    }
  };

  return (
    <ChatLayout
      isLoading={isLoading}
      isAnalyzing={isAnalyzing}
      onFileUpload={handleFileAnalysis}
      onPurchase={() => {}}
    >
      <motion.div 
        className="flex-1 flex flex-col min-h-0 h-[calc(100dvh-4rem)] md:h-[calc(100dvh-8rem)] overflow-hidden bg-gradient-to-b from-background to-background/80 relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <RestorePrompt 
          showRestorePrompt={showRestorePrompt}
          onStartNewChat={handleStartNewChat}
          onRestoreChat={handleRestoreChat}
        />

        <div className="flex-1 flex flex-col md:flex-row gap-2 md:gap-4 p-0 md:p-4 min-h-0 overflow-hidden relative w-full">
          <motion.div 
            className={cn(
              "flex-1 flex flex-col min-h-0 overflow-hidden",
              showArchive || showConsent ? 'hidden md:flex' : 'flex'
            )}
            layout
          >
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-2 py-2 md:px-4 md:py-3 border-b">
              <ChatActions
                onNewChat={resetChat}
                onSaveChat={handleSaveChatWrapper}
                onToggleArchive={() => {
                  setShowArchive(!showArchive);
                  setShowConsent(false);
                }}
                showArchive={showArchive}
                messagesExist={messagesExist}
              />
            </div>

            <ChatContainer 
              messages={messages}
              userInput={userInput}
              isLoading={isLoading}
              isAnalyzing={isAnalyzing}
              isGenerating={isGenerating}
              progress={progress}
              canShowServices={canShowServices}
              onUserInputChange={setUserInput}
              onSendMessage={handleSendMessage}
              onCreateDocument={handleCreateDocument}
              onCreateCase={handleCreateCase}
              onLawSearch={handleLawSearch}
              onFileUpload={() => document.getElementById('file-upload')?.click()}
              onToggleConsent={() => {
                setShowConsent(!showConsent);
                setShowArchive(false);
              }}
              showConsent={showConsent}
            />
          </motion.div>

          <AnimatePresence>
            {showArchive && (
              <motion.div 
                className="absolute inset-0 md:relative md:w-80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-20 overflow-y-auto border-l shadow-lg"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <ChatArchive
                  savedChats={savedChats}
                  onSelectChat={handleLoadChatWrapper}
                  onDeleteChat={handleDeleteChat}
                  onRenameChat={handleRenameChat}
                  onClose={() => setShowArchive(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showConsent && (
              <motion.div 
                className="absolute inset-0 md:relative md:w-96 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-20 overflow-y-auto border-l shadow-lg p-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex justify-between items-center mb-4 md:hidden">
                  <h2 className="text-lg font-semibold">Personverninnstillinger</h2>
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConsent(false)}
                  >
                    <span className="sr-only">Lukk</span>
                    ×
                  </button>
                </div>
                <AIConsentManager />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </ChatLayout>
  );
}